'use client';

import { useRef, forwardRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogContent, Button, Box, Slide, AppBar, Toolbar, Typography } from '@mui/material';
import L from 'leaflet';

// Leaflet assets
import 'leaflet/dist/leaflet.css';
import 'leaflet-control-geocoder/dist/Control.Geocoder.css';
import 'leaflet-control-geocoder';

// Import Geoman
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';

import { baseMaps } from '../utils/mapConfig';

// Fix icon paths
try {
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: '/marker-icon-2x.png',
    iconUrl: '/marker-icon.png',
    shadowUrl: '/marker-shadow.png'
  });
} catch (e) {
  console.log(e);
}

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function DrawPolygon({
  open,
  title = 'Draw Location',
  initialGeoJSON = null,
  onClose,
  onSave,
  center = [-0.789275, 113.921327],
  zoom = 5,
  position = 'topleft',
  singleFeature = true
}) {
  const mapRef = useRef(null);
  const drawnItemsRef = useRef(null);
  const mapContainerRef = useRef(null);

  const ensureControlStyle = () => {
    const id = 'leaflet-control-custom-style';
    if (document.getElementById(id)) return;
    const style = document.createElement('style');
    style.id = id;
    style.innerHTML = `
      /* 
       * HAPUS margin-top hardcode agar Leaflet bisa menumpuk kontrol secara otomatis
       * sehingga ketika Geocoder dilebarkan, kontrol di bawahnya akan otomatis terdorong 
       */
      .leaflet-top.leaflet-left .leaflet-control {
        margin-top: 10px;
        margin-left: 10px;
      }

      /* Custom Geoman Icons Color (Ungu) */
      .leaflet-pm-toolbar .leaflet-buttons-control-button {
         color: #485D9A !important;
      }
      .leaflet-pm-toolbar .leaflet-buttons-control-button .control-icon {
         fill: #485D9A !important;
         stroke: #485D9A !important;
      }
      .leaflet-pm-toolbar .active .control-icon {
         fill: white !important; 
         stroke: white !important;
      }
      .leaflet-pm-toolbar .active {
         background-color: #485D9A !important;
      }
    `;
    document.head.appendChild(style);
  };

  const makeLayerEditableOnClick = (layer, map) => {
    layer.on('click', (e) => {
      L.DomEvent.stopPropagation(e);
      if (drawnItemsRef.current) {
        drawnItemsRef.current.eachLayer((l) => {
          if (l.pm && l !== layer) l.pm.disable();
        });
      }
      if (layer.pm) {
        layer.pm.enable({
          allowSelfIntersection: false,
          preventMarkerRemoval: false
        });
      }
    });
  };

  const handleDialogEntered = () => {
    if (mapRef.current) return;

    const container = mapContainerRef.current || document.getElementById('map_dialog_container');
    if (!container) {
      console.warn('Map container not found.');
      return;
    }
    const pos = (position || 'topleft').toLowerCase();

    const defaultLayer = baseMaps['Google Map'] || Object.values(baseMaps)[0];
    const map = L.map(container, {
      center,
      zoom,
      zoomControl: false,
      attributionControl: false,
      layers: [defaultLayer]
    });
    mapRef.current = map;

    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);
    drawnItemsRef.current = drawnItems;

    map.on('click', () => {
      drawnItems.eachLayer((l) => {
        if (l.pm) l.pm.disable();
      });
    });

    if (initialGeoJSON) {
      try {
        L.geoJSON(initialGeoJSON, {
          onEachFeature: (feature, l) => {
            drawnItems.addLayer(l);
            makeLayerEditableOnClick(l, map);
          }
        });
      } catch (err) {
        console.warn('Failed to load initialGeoJSON', err);
      }
    }

    ensureControlStyle();

    if (L.Control.Geocoder) {
      L.Control.geocoder({
        position: pos,
        collapsed: true,
        placeholder: 'Cari lokasi...'
      }).addTo(map);
    }

    // Geoman Tools
    map.pm.addControls({
      position: pos,
      drawPolygon: true,
      drawRectangle: true,
      drawPolyline: false,
      drawCircle: false,
      drawMarker: false,
      drawCircleMarker: false,
      drawText: false,
      editMode: true,
      dragMode: true,
      cutPolygon: false,
      removalMode: true,
      rotateMode: false
    });

    map.pm.setPathOptions({
      color: '#485D9A',
      fillColor: '#485D9A',
      fillOpacity: 0.4
    });

    L.control.zoom({ position: pos }).addTo(map);

    map.on('pm:create', (e) => {
      if (singleFeature) {
        drawnItems.clearLayers();

        map.eachLayer((l) => {
          if (l.pm && l.pm._shape && l !== e.layer && l !== defaultLayer) {
            map.removeLayer(l);
          }
        });
      }

      drawnItems.addLayer(e.layer);
      makeLayerEditableOnClick(e.layer, map);
    });

    map.on('pm:remove', (e) => {
      drawnItems.removeLayer(e.layer);
    });

    L.control
      .layers(baseMaps, null, {
        position: 'topright',
        collapsed: true
      })
      .addTo(map);

    setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.invalidateSize();
        if (initialGeoJSON && drawnItemsRef.current && drawnItemsRef.current.getLayers().length > 0) {
          const b = drawnItemsRef.current.getBounds();
          if (b && b.isValid() && !b.isEmpty()) {
            mapRef.current.fitBounds(b, { padding: [50, 50], maxZoom: 16 });
          }
        }
      }
    }, 200);
  };

  const handleDialogExited = () => {
    if (mapRef.current) {
      mapRef.current.pm.removeControls();
      mapRef.current.remove();
      mapRef.current = null;
      drawnItemsRef.current = null;
    }
  };

  const handleSave = () => {
    const drawnItems = drawnItemsRef.current;
    if (drawnItems) {
      drawnItems.eachLayer((l) => {
        if (l.pm) l.pm.disable();
      });
    }

    if (mapRef.current) {
      mapRef.current.eachLayer((layer) => {
        if (layer.pm && layer.pm._shape && !drawnItems.hasLayer(layer)) {
          drawnItems.addLayer(layer);
        }
      });
    }

    if (!drawnItems || drawnItems.getLayers().length === 0) {
      onSave?.(null);
      onClose?.();
      return;
    }

    const layers = drawnItems.getLayers();
    let geojson = null;
    if (layers.length === 1) {
      geojson = layers[0].toGeoJSON();
    } else {
      geojson = {
        type: 'FeatureCollection',
        features: layers.map((l) => l.toGeoJSON())
      };
    }
    onSave?.(geojson);
    onClose?.();
  };

  useEffect(() => {
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  return (
    <Dialog
      open={!!open}
      fullWidth
      maxWidth="xl"
      fullScreen
      onClose={onClose}
      TransitionComponent={Transition}
      TransitionProps={{
        onEntered: handleDialogEntered,
        onExited: handleDialogExited
      }}
    >
      <AppBar sx={{ position: 'relative', backgroundColor: 'primary.main' }}>
        <Toolbar>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h4" component="div">
            {title}
          </Typography>
          <Box>
            <Button
              onClick={onClose}
              sx={{
                color: 'rgba(255,255,255,0.85)',
                '&:hover': {
                  color: 'rgba(255,255,255 )'
                },
                textTransform: 'none'
              }}
            >
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSave}>
              Save
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <DialogContent sx={{ height: '75vh', p: 0 }}>
        <Box id="map_dialog_container" ref={mapContainerRef} sx={{ width: '100%', height: '100%' }} />
      </DialogContent>
    </Dialog>
  );
}

DrawPolygon.propTypes = {
  open: PropTypes.bool,
  title: PropTypes.string,
  initialGeoJSON: PropTypes.object,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  center: PropTypes.array,
  zoom: PropTypes.number,
  position: PropTypes.string,
  singleFeature: PropTypes.bool
};
